import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { exportCustomCrosstabs } from '@api/survey_dashboard';

import { Button, Error as ErrorView, Layout, Loader, toast } from '@utilities';

import BannerCanvas from './components/BannerCanvas';
import RowNetEditor from './components/RowNetEditor';
import SettingsPanel from './components/SettingsPanel';
import SourceRail from './components/SourceRail';
import TablesList from './components/TablesList';
import ValuePicker from './components/ValuePicker';

import {
  DEFAULT_BASE_THRESHOLD,
  DEFAULT_CONFIDENCE,
  FILTER_LOGIC,
  MAX_BANNER_COLUMNS,
  SOURCE_CATEGORIES,
} from './utilities/constants';
import {
  buildBannerSources,
  buildDefaultQuestionConfigs,
  buildExportPayload,
  combineSpanPoints,
  countBannerColumns,
  createId,
  createSpanFromSource,
  dissolveSpanPoint,
  getEligibleQuestions,
  getIneligibleQuestions,
  getQuestionLabel,
  getQuestionOptionValues,
  removeSpanPoint,
  renameSpanPoint,
  sourceNeedsValuePicker,
} from './utilities/helpers';

import styles from './_index.module.scss';

const CrosstabBuilder = ({
  banner,
  globalFilters,
  globalFiltersError,
  isFiltersLoading,
  isRawDataLoading,
  questionConfigs,
  rawData,
  setBanner,
  setQuestionConfigs,
  surveyId,
  surveyQuestions,
}) => {
  const [baseThresholdInput, setBaseThresholdInput] = useState(String(DEFAULT_BASE_THRESHOLD));
  const [confidence, setConfidence] = useState(DEFAULT_CONFIDENCE);
  const [filterLogic, setFilterLogic] = useState(FILTER_LOGIC.AND);
  const [filterRows, setFilterRows] = useState([]);
  const [isExporting, setIsExporting] = useState(false);
  const [netEditor, setNetEditor] = useState(null);
  const [pickerSource, setPickerSource] = useState(null);

  const sources = useMemo(
    () => buildBannerSources({ globalFilters, rawData, surveyQuestions }),
    [globalFilters, rawData, surveyQuestions]
  );
  const eligibleQuestions = useMemo(() => getEligibleQuestions(surveyQuestions), [surveyQuestions]);
  const ineligibleQuestions = useMemo(
    () => getIneligibleQuestions(surveyQuestions),
    [surveyQuestions]
  );
  const filterSources = useMemo(
    () =>
      sources.filter(
        ({ category, isGroupedPreset }) =>
          !isGroupedPreset && category !== SOURCE_CATEGORIES.OVERALL
      ),
    [sources]
  );

  useEffect(() => {
    if (questionConfigs.length === 0) {
      setQuestionConfigs(buildDefaultQuestionConfigs(surveyQuestions));
    }
  }, [surveyQuestions]);

  const parsedBaseThreshold = Number.parseInt(baseThresholdInput, 10);
  const baseThreshold = Number.isNaN(parsedBaseThreshold)
    ? DEFAULT_BASE_THRESHOLD
    : Math.max(1, parsedBaseThreshold);

  const selectedSourceIds = banner.map(({ sourceId }) => sourceId);
  const columnCount = countBannerColumns(banner);
  const includedQuestionCount = questionConfigs.filter(({ included }) => included).length;
  const isOverColumnCap = columnCount > MAX_BANNER_COLUMNS;
  const canExport =
    !isExporting && columnCount > 0 && includedQuestionCount > 0 && !isOverColumnCap;

  const handleToggleSource = (source) => {
    const existing = banner.find(({ sourceId }) => sourceId === source.id);
    if (existing) {
      setBanner((prev) => prev.filter(({ sourceId }) => sourceId !== source.id));
      return;
    }
    if (sourceNeedsValuePicker(source)) {
      setPickerSource(source);
      return;
    }
    setBanner((prev) => [...prev, createSpanFromSource(source)]);
  };

  const handlePickerConfirm = ({ selectedValues, withAllOthers }) => {
    setBanner((prev) => [
      ...prev,
      createSpanFromSource(pickerSource, { selectedValues, withAllOthers }),
    ]);
    setPickerSource(null);
  };

  const handleRemoveSpan = (spanId) => {
    setBanner((prev) => prev.filter(({ id }) => id !== spanId));
  };

  const handleUpdateSpan = (spanId, action) => {
    setBanner((prev) =>
      prev
        .map((span) => {
          if (span.id !== spanId) return span;
          if (action.type === 'combine')
            return combineSpanPoints(span, action.pointKeys, action.name);
          if (action.type === 'dissolve') return dissolveSpanPoint(span, action.pointKey);
          if (action.type === 'rename') return renameSpanPoint(span, action.pointKey, action.name);
          if (action.type === 'remove-point') return removeSpanPoint(span, action.pointKey);
          return span;
        })
        .filter(({ points }) => points.length > 0)
    );
  };

  const handleToggleQuestion = (questionId) => {
    setQuestionConfigs((prev) =>
      prev.map((config) =>
        config.questionId === questionId ? { ...config, included: !config.included } : config
      )
    );
  };

  const handleRankTopNChange = (questionId, rankTopN) => {
    setQuestionConfigs((prev) =>
      prev.map((config) => (config.questionId === questionId ? { ...config, rankTopN } : config))
    );
  };

  const handleEditNet = (questionId, netId) => {
    setNetEditor({ netId, questionId });
  };

  const handleRemoveNet = (questionId, netId) => {
    setQuestionConfigs((prev) =>
      prev.map((config) =>
        config.questionId === questionId
          ? { ...config, nets: config.nets.filter(({ id }) => id !== netId) }
          : config
      )
    );
  };

  const handleSaveNet = ({ name, optionIds }) => {
    const { netId, questionId } = netEditor;
    setQuestionConfigs((prev) =>
      prev.map((config) => {
        if (config.questionId !== questionId) return config;
        if (netId) {
          return {
            ...config,
            nets: config.nets.map((net) => (net.id === netId ? { ...net, name, optionIds } : net)),
          };
        }
        return { ...config, nets: [...config.nets, { id: createId('net'), name, optionIds }] };
      })
    );
    setNetEditor(null);
  };

  const handleBaseThresholdChange = (value) => {
    setBaseThresholdInput(value);
  };

  const handleBaseThresholdBlur = () => {
    setBaseThresholdInput(String(baseThreshold));
  };

  const handleAddFilterRow = () => {
    setFilterRows((prev) => [...prev, { id: createId('filter'), sourceId: null, values: [] }]);
  };

  const handleRemoveFilterRow = (rowId) => {
    setFilterRows((prev) => prev.filter(({ id }) => id !== rowId));
  };

  const handleFilterRowSourceChange = (rowId, sourceId) => {
    setFilterRows((prev) =>
      prev.map((row) => (row.id === rowId ? { ...row, sourceId, values: [] } : row))
    );
  };

  const handleFilterRowValueToggle = (rowId, valueId) => {
    setFilterRows((prev) =>
      prev.map((row) => {
        if (row.id !== rowId) return row;
        const values = row.values.includes(valueId)
          ? row.values.filter((value) => value !== valueId)
          : [...row.values, valueId];
        return { ...row, values };
      })
    );
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await exportCustomCrosstabs({
        payload: buildExportPayload({
          banner,
          baseThreshold,
          confidence,
          filterLogic,
          filterRows,
          filterSources,
          questionConfigs,
        }),
        surveyId,
      });
      toast(
        'Your crosstab export is being prepared. Click the bell icon in the top right corner to download it.'
      );
    } catch (error) {
      console.error('Error occurred while exporting custom crosstabs', error);
      toast.error('Error occurred while exporting custom crosstabs.');
    }
    setIsExporting(false);
  };

  // Trip-attribute sources are derived from the raw data table, so the builder
  // waits for that fetch too - otherwise the category silently pops in later.
  if (isFiltersLoading || isRawDataLoading) return <Loader isCentered />;
  if (globalFiltersError)
    return (
      <ErrorView
        description="Failed to load the filters needed to build a crosstab."
        header="Something went wrong"
        status={globalFiltersError?.response?.status}
      />
    );

  const editorQuestion = netEditor
    ? eligibleQuestions.find(({ id }) => String(id) === netEditor.questionId)
    : null;
  const editorConfig = netEditor
    ? questionConfigs.find(({ questionId }) => questionId === netEditor.questionId)
    : null;
  const editorNet = editorConfig?.nets.find(({ id }) => id === netEditor?.netId) ?? null;

  return (
    <Layout.Flex className={styles['crosstab-builder']} gap="large">
      <aside className={styles['crosstab-builder-rail']}>
        <SourceRail
          onToggleSource={handleToggleSource}
          selectedSourceIds={selectedSourceIds}
          sources={sources}
        />
      </aside>

      <main className={styles['crosstab-builder-canvas']}>
        <div className={styles['crosstab-builder-toolbar']}>
          <h3>Banner</h3>
        </div>
        <BannerCanvas
          banner={banner}
          onRemoveSpan={handleRemoveSpan}
          onReorderSpans={setBanner}
          onUpdateSpan={handleUpdateSpan}
        />
        {isOverColumnCap && (
          <p className={styles['crosstab-builder-warning']}>
            The banner has {columnCount} columns; the export supports up to {MAX_BANNER_COLUMNS}.
            Remove columns to export.
          </p>
        )}

        <div className={styles['crosstab-builder-toolbar']}>
          <h3>Tables to export</h3>
        </div>
        <TablesList
          ineligibleQuestions={ineligibleQuestions}
          onEditNet={handleEditNet}
          onRankTopNChange={handleRankTopNChange}
          onRemoveNet={handleRemoveNet}
          onToggleQuestion={handleToggleQuestion}
          questionConfigs={questionConfigs}
          questions={eligibleQuestions}
        />
      </main>

      <aside className={styles['crosstab-builder-inspector']}>
        <Button
          isDisabled={!canExport}
          isLoading={isExporting}
          onClick={handleExport}
          text="Export to Excel"
        />
        <p className={styles['crosstab-builder-hint']}>
          The export renders every included question against the banner, with inline significance
          letters.
        </p>
        <section className={styles['crosstab-builder-summary']} data-testid="export-package">
          <h4>Export Package</h4>
          <dl>
            <div>
              <dt>Columns</dt>
              <dd>{columnCount}</dd>
            </div>
            <div>
              <dt>Questions</dt>
              <dd>{includedQuestionCount}</dd>
            </div>
          </dl>
        </section>
        <SettingsPanel
          baseThresholdInput={baseThresholdInput}
          confidence={confidence}
          filterLogic={filterLogic}
          filterRows={filterRows}
          filterSources={filterSources}
          onAddFilterRow={handleAddFilterRow}
          onBaseThresholdBlur={handleBaseThresholdBlur}
          onBaseThresholdChange={handleBaseThresholdChange}
          onConfidenceChange={setConfidence}
          onFilterLogicChange={setFilterLogic}
          onFilterRowSourceChange={handleFilterRowSourceChange}
          onFilterRowValueToggle={handleFilterRowValueToggle}
          onRemoveFilterRow={handleRemoveFilterRow}
        />
      </aside>

      {pickerSource && (
        <ValuePicker
          onCancel={() => setPickerSource(null)}
          onConfirm={handlePickerConfirm}
          source={pickerSource}
        />
      )}
      {netEditor && editorQuestion && (
        <RowNetEditor
          net={editorNet}
          onCancel={() => setNetEditor(null)}
          onSave={handleSaveNet}
          options={getQuestionOptionValues(editorQuestion)}
          questionLabel={getQuestionLabel(editorQuestion)}
        />
      )}
    </Layout.Flex>
  );
};

CrosstabBuilder.propTypes = {
  banner: PropTypes.array.isRequired,
  globalFilters: PropTypes.object,
  globalFiltersError: PropTypes.object,
  isFiltersLoading: PropTypes.bool,
  isRawDataLoading: PropTypes.bool,
  questionConfigs: PropTypes.array.isRequired,
  rawData: PropTypes.array,
  setBanner: PropTypes.func.isRequired,
  setQuestionConfigs: PropTypes.func.isRequired,
  surveyId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  surveyQuestions: PropTypes.arrayOf(PropTypes.object),
};

export default CrosstabBuilder;
